import { useState, useCallback, useMemo } from 'react';
import { PAGINATION } from '../utils/constants';

/**
 * Custom hook for handling pagination
 * @param {array} items - Array of items to paginate
 * @param {number} initialPage - Initial page number (1-based)
 * @param {number} initialPageSize - Initial page size
 * @returns {object} Pagination state and handlers
 */
const usePagination = (
  items = [],
  initialPage = 1,
  initialPageSize = PAGINATION.DEFAULT_PAGE_SIZE
) => {
  const [page, setPage] = useState(initialPage);
  const [pageSize, setPageSize] = useState(initialPageSize);
  
  // Calculate total pages
  const totalPages = useMemo(() => {
    return Math.ceil(items.length / pageSize) || 1;
  }, [items.length, pageSize]);
  
  // Ensure current page is within bounds
  const currentPage = useMemo(() => {
    return Math.min(Math.max(1, page), totalPages);
  }, [page, totalPages]);
  
  // Get current page items
  const currentItems = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return items.slice(startIndex, endIndex);
  }, [items, currentPage, pageSize]);
  
  // Handle page change
  const handlePageChange = useCallback((newPage) => {
    setPage(Math.min(Math.max(1, newPage), totalPages));
  }, [totalPages]);
  
  // Handle page size change
  const handlePageSizeChange = useCallback((newPageSize) => {
    // Calculate new page number to keep the first visible item the same
    const firstItemIndex = (currentPage - 1) * pageSize;
    const newPage = Math.floor(firstItemIndex / newPageSize) + 1;
    
    setPageSize(newPageSize);
    setPage(newPage);
  }, [currentPage, pageSize]);
  
  // Go to first page
  const goToFirstPage = useCallback(() => {
    setPage(1);
  }, []);
  
  // Go to last page
  const goToLastPage = useCallback(() => {
    setPage(totalPages);
  }, [totalPages]);
  
  // Go to next page
  const goToNextPage = useCallback(() => {
    if (currentPage < totalPages) {
      setPage(currentPage + 1);
    }
  }, [currentPage, totalPages]);
  
  // Go to previous page
  const goToPreviousPage = useCallback(() => {
    if (currentPage > 1) {
      setPage(currentPage - 1);
    }
  }, [currentPage]);
  
  // Reset pagination
  const resetPagination = useCallback(() => {
    setPage(initialPage);
    setPageSize(initialPageSize);
  }, [initialPage, initialPageSize]);
  
  // Calculate pagination info
  const paginationInfo = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = Math.min(startIndex + pageSize, items.length);
    
    return {
      currentPage,
      totalPages,
      pageSize,
      totalItems: items.length,
      startIndex: startIndex + 1,
      endIndex,
      hasPreviousPage: currentPage > 1,
      hasNextPage: currentPage < totalPages,
    };
  }, [currentPage, totalPages, pageSize, items.length]);
  
  return {
    currentItems,
    paginationInfo,
    handlePageChange,
    handlePageSizeChange,
    goToFirstPage,
    goToLastPage,
    goToNextPage,
    goToPreviousPage,
    resetPagination,
    pageSizeOptions: PAGINATION.PAGE_SIZE_OPTIONS,
  };
};

export default usePagination;
